// useAuthService.js
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import PageRoutes from "../utils/routes";
import useAuth from "../hooks/useAuth";
import { message } from "antd";
import { handleError, handleResponse } from "../utils/handleResponses";
import Endpoints from "../api/endpoints";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import EndpointFunctions from "../utils/Functions/endpointFunctions";

const PERSONNEL_REGISTER_URL =
  "https://hemenpersonel-api.anemotech.com/personnel/auth/registerWeb";
const PERSONNEL_LOGIN_URL =
  "https://hemenpersonel-api.anemotech.com/personnel/auth/authenticateWeb";
const FIRM_REGISTER_URL =
  "https://hemenpersonel-api.anemotech.com/firm/auth/registerWeb";
const FIRM_LOGIN_URL =
  "https://hemenpersonel-api.anemotech.com/firm/auth/authenticateWeb";
const FIRM_GOOGLE_LOGIN_URL =
  "https://hemenpersonel-api.anemotech.com/firm/auth/googleAuthenticateWeb";
const PERSONNEL_GOOGLE_LOGIN_URL =
  "https://hemenpersonel-api.anemotech.com/personnel/auth/googleAuthenticateWeb";

const useAuthService = () => {
  const { setFirmAuth, setPersonnelAuth, logoutPersonnel, logoutFirm } =
    useAuth(); // Custom hook for auth state management
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = location.state?.from?.pathname || PageRoutes.home;
  const axiosPrivate = useAxiosPrivate();

  // PERSONNEL REGISTRATION
  const personnelRegister = useMutation({
    mutationFn: async (values) => {
      const response = await axios.post(PERSONNEL_REGISTER_URL, values, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return handleResponse(response);
    },
    onSuccess: (data) => {
      const { accessToken, refreshToken, user } = data;
      setPersonnelAuth({ accessToken, refreshToken, user });
      navigate(redirectPath, { replace: true });
      message.success("Personnel registered successfully!");
    },
    onError: (error) => {
      message.error(handleError({ error }).errorMessage);
    },
  });

  // PERSONNEL LOGIN
  const personnelLogin = useMutation({
    mutationFn: async (values) => {
      const response = await axios.post(PERSONNEL_LOGIN_URL, values, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return response;
    },
    onSuccess: (data) => {
      const { accessToken, refreshToken, user } = data.data.data;
      console.log("data is----", data);
      setPersonnelAuth({ accessToken, refreshToken, user });
      navigate(PageRoutes.personnelHome, { replace: true });
      message.success("Personnel login successful!");
    },
    onError: (error) => {
      message.error(handleError({ error }).errorMessage);
    },
  });

  // PERSONNEL GOOGLE LOGIN
  const personnelGoogleLogin = useMutation({
    mutationFn: async (values) => {
      const response = await axios.post(PERSONNEL_GOOGLE_LOGIN_URL, values, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return response;
    },
    onSuccess: (data) => {
      const { accessToken, refreshToken, user } = data.data.data;
      setPersonnelAuth({ accessToken, refreshToken, user });
      navigate(PageRoutes.personnelHome, { replace: true });
      message.success("Personnel login successful!");
    },
    onError: (error) => {
      message.error(handleError({ error }).errorMessage);
    },
  });

  // PERSONNEL LOGOUT
  const personnelLogout = useMutation({
    mutationFn: async () => {
      const response = await EndpointFunctions.postData({
        endpointURL: Endpoints.personnelLogout,
        axiosPrivate: axiosPrivate,
      });
    },
    onSuccess: () => {
      logoutPersonnel();
      navigate(PageRoutes.home, { replace: true });
      message.success("Başarıyla Çıkış Yapıldı!");
    },
    onError: (error) => {
      message.error(handleError({ error }).errorMessage);
    },
  });

  // FIRM REGISTRATION
  const firmRegister = useMutation({
    mutationFn: async (values) => {
      const response = await axios.post(FIRM_REGISTER_URL, values, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return handleResponse(response);
    },
    onSuccess: (data) => {
      const { accessToken, refreshToken, user } = data;
      setFirmAuth({ accessToken, refreshToken, user });
      navigate(PageRoutes.personnelHome, { replace: true });
      message.success("Firm registered successfully!");
    },
    onError: (error) => {
      message.error(handleError({ error }).errorMessage);
    },
  });

  // FIRM LOGIN
  const firmLogin = useMutation({
    mutationFn: async (values) => {
      const response = await axios.post(FIRM_LOGIN_URL, values, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return response;
    },
    onSuccess: (data) => {
      const { accessToken, refreshToken, user } = data.data.data;
      console.log("data is----", data);
      setFirmAuth({ accessToken, refreshToken, user });
      navigate(PageRoutes.firmHome, { replace: true });
      message.success("Firm login successful!");
    },
    onError: (error) => {
      message.error(handleError({ error }).errorMessage);
    },
  });

  // FIRM LOGOUT
  const firmLogout = useMutation({
    mutationFn: async () => {
      const response = await EndpointFunctions.postData({
        endpointURL: Endpoints.firmLogout,
        axiosPrivate: axiosPrivate,
      });
    },
    onSuccess: () => {
      logoutFirm();
      navigate(PageRoutes.home, { replace: true });
      message.success("Başarıyla Çıkış Yapıldı!");
    },
    onError: (error) => {
      message.error(handleError({ error }).errorMessage);
    },
  });

  // FIRM GOOGLE LOGIN
  const firmGoogleLogin = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(FIRM_GOOGLE_LOGIN_URL, data);
      return response.data;
    },
    onSuccess: (data) => {
      const { accessToken, refreshToken, user } = data.data;
      setFirmAuth({ accessToken, refreshToken, user });
      navigate(PageRoutes.firmHome, { replace: true });
      message.success("Firm registered successfully!");
    },
    onError: (error) => {
      message.error(handleError({ error }).errorMessage);
    },
  });

  return {
    personnelRegister,
    isPersonnelRegisterLoading: personnelRegister.isPending,
    //---------

    personnelLogin,
    isPersonnelLoginLoading: personnelLogin.isPending,
    //---------

    firmRegister,
    isFirmRegisterLoading: firmRegister.isPending,
    //---------

    firmLogin,
    isFirmLoginLoading: firmLogin.isPending,

    //--------
    personnelGoogleLogin,
    isGoogleSignInLoading: personnelGoogleLogin.isPending,

    personnelLogout,
    firmLogout,

    firmGoogleLogin,
  };
};

export default useAuthService;
